import { useParams, Link, Navigate } from "react-router-dom";
import Layout, { Breadcrumb } from "@/components/Layout";
import SEO from "@/components/SEO";
import { getLegalPages, getLegalNav } from "@/lib/legalContent";
import { pageOg } from "@/lib/cdn";
import { useT, useLang } from "@/lib/i18n";

const LegalPage = () => {
  const t = useT();
  const lang = useLang();
  const { slug } = useParams();
  const pages = getLegalPages(lang);
  const nav = getLegalNav(lang);
  const page = pages[slug];
  if (!page) return <Navigate to="/legal/legal-notice" replace />;

  // Map legal slugs to OG image slugs as per spec
  const ogSlug = slug === "legal-notice" ? "legal" : slug;

  return (
    <Layout testId={`page-legal-${slug}`}>
      <SEO title={page.title} description={page.intro} image={pageOg(ogSlug)} />
      <section className="pt-10 md:pt-16 pb-8">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <Breadcrumb items={[{ to: "/", label: t("common.home") }, { label: t("legal.eyebrow") }, { label: page.title }]} />
          <div className="mt-5">
            <div className="eyebrow mb-3">{t("legal.eyebrow")}</div>
            <h1 className="font-display text-[36px] md:text-[52px] leading-[1.08] text-[color:var(--gv-ink)] mb-3">
              {page.title}
            </h1>
            <div className="text-[13px] text-[color:var(--gv-ink)]/55 mb-4">{page.updated}</div>
            {page.intro && <p className="text-[15.5px] text-[color:var(--gv-ink)]/70">{page.intro}</p>}
          </div>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Side nav */}
          <aside className="lg:col-span-3">
            <div className="sticky top-24 border-l border-[color:var(--gv-line)] pl-4">
              <div className="eyebrow mb-4">{t("legal.sections")}</div>
              <ul className="space-y-2 text-[13.5px]">
                {nav.map((l) => (
                  <li key={l.slug}>
                    <Link
                      to={`/legal/${l.slug}`}
                      className={`block py-1 ${l.slug === slug ? "text-[color:var(--gv-pink)] font-medium" : "text-[color:var(--gv-ink)]/70 hover:text-[color:var(--gv-pink)]"}`}
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Content */}
          <article className="lg:col-span-9 space-y-10">
            {page.sections.map((s, i) => (
              <div key={i}>
                <h2 className="font-display text-[22px] md:text-[26px] text-[color:var(--gv-ink)] mb-4">{s.h}</h2>
                <div className="space-y-3 text-[15px] leading-[1.8] text-[color:var(--gv-ink)]/80">
                  {s.p.map((p, j) => <p key={j}>{p}</p>)}
                </div>
              </div>
            ))}

            <div className="pt-6 border-t border-[color:var(--gv-line)] text-[12.5px] text-[color:var(--gv-ink)]/55">
              {t("legal.contact_q")} <a href="mailto:hello@etoh.io" className="text-[color:var(--gv-pink)]">hello@etoh.io</a>.
            </div>
          </article>
        </div>
      </section>
    </Layout>
  );
};

export default LegalPage;
